// Thẻ của hộp hướng dẫn trang Khiếu nại — MỘT nguồn, buộc vào nhãn thật.
//
// Nhãn lấy thẳng từ `COMPLAINTS_TAB_LABEL` của màn, và danh sách khoá khai
// `satisfies` theo đúng khoá của từ điển đó: đổi khoá thẻ trên màn là lỗi kiểu
// ngay lúc build, không phải đợi ai mở hộp lên mới thấy bài chỉ tới một thẻ
// không còn tồn tại.

import type { COMPLAINTS_TAB_LABEL } from "@/features/complaints/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang trong `nav-items.ts`. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const COMPLAINTS_GUIDE_ID = "complaints";

/** Thứ tự thẻ trong hộp = thứ tự thẻ trên màn. */
export const COMPLAINTS_GUIDE_TAB_ORDER = [
  "voice",
  "tickets",
] as const satisfies readonly (keyof typeof COMPLAINTS_TAB_LABEL)[];

export type ComplaintsGuideTabKey = (typeof COMPLAINTS_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 * 🔴 Hai thẻ của trang này KHÔNG cùng phạm vi, và đó là chỗ đáng canh nhất.
 * Nguồn của thẻ đầu: `features/customer-voice/permissions.ts` (`canReadFeedback`)
 * + cổng máy chủ `AllowOrDept("feedback.read", "cskh", "reception", "sale")`.
 * Nguồn của thẻ sau: ma trận `page-access.ts` cho trang `complaints`.
 *
 * Đo 25/09/2026: 11 vai vào được trang mà CỐ Ý không vào được sổ Tiếng khách —
 * hậu kỳ, trang điểm, thợ chụp, kế toán viên thường, marketing, phụ studio,
 * nhân viên chưa gán phòng. Khai thiếu ở đây là hộp bày cho họ một thẻ mà màn
 * thật không cho mở; khai thừa thì ngược lại, họ đọc trọn bài của một thẻ
 * không thuộc về mình.
 */
export const THE_AI_MO_DUOC: Record<ComplaintsGuideTabKey, Audience[]> = {
  // Sổ tiếng khách: ba phòng ghi được + các vai mang quyền đọc sổ.
  voice: ["cskh", "reception", "sale", "qc", "accounting", "lead", "exec"],
  // Phiếu khiếu nại: gần như ai vào được trang cũng mở được, vì phiếu chạm
  // tới việc của nhiều phòng (làm lại ảnh, chụp bù, hoàn tiền).
  tickets: [
    "cskh",
    "reception",
    "sale",
    "photo",
    "makeup",
    "assistant",
    "retouch",
    "marketing",
    "accounting",
    "qc",
    "lead",
    "exec",
  ],
};

/** Màu nhãn trạng thái, khớp badge trên màn thật. */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  // Sổ tiếng khách
  "Chờ phản hồi": "warning",
  "Đang xử lý": "info",
  "Đã xong": "success",
  "Đã lập phiếu": "info",
  "Mức Nặng": "danger",
  // Phiếu khiếu nại — nhãn cột "Hạn xử lý" (features/complaints/deadlines.ts)
  "Còn … để có phương án": "info",
  "Còn … để đóng": "info",
  "Trễ hạn có phương án 24 giờ": "danger",
  "Trễ hạn đóng 48 giờ": "danger",
  "Chờ kế toán chi hoàn": "warning",
  "Khách chưa đồng ý": "warning",
  "⏳ Chờ kế toán": "warning",
};

/**
 * Vòng đời một vụ của khách, chạy xuyên hai thẻ.
 *
 * Dải này là lý do đáng kể nhất để gộp hai màn: đứng ở một thẻ thì không thấy
 * được vì sao một dòng biến khỏi sổ rồi hiện ra thành phiếu. Hai chặng cuối
 * cố ý là hai NHÁNH chứ không nối tiếp — phần lớn vụ đóng ngay trong sổ, chỉ
 * vụ phải làm lại cho khách mới đi tiếp sang phiếu.
 */
const COMPLAINTS_FLOW: GuideFlow = {
  heading: "Một vụ của khách đi qua đâu",
  intro:
    "Hai thẻ của trang là hai chặng của cùng một vụ. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Khách nói",
      tone: "warning",
      ke: "Lời khách vào sổ — dù khách gọi điện, nói tại quầy, bấm trên cổng hay để lại nhận xét; vụ chưa gắn đơn nào cũng ghi được.",
      the: ["voice"],
    },
    {
      ten: "Đã phản hồi",
      tone: "info",
      ke: "Có người gọi lại cho khách và ghi nhận mốc đó; từ lúc này vụ mới đi tiếp được.",
      the: ["voice"],
    },
    {
      ten: "Kết luận trong sổ",
      tone: "success",
      nhanh: true,
      ke: "Phần lớn vụ dừng ở đây: nói cho khách hiểu, ghi kết quả, đóng dòng.",
      the: ["voice"],
    },
    {
      ten: "Lập phiếu",
      tone: "info",
      ke: "Vụ phải làm lại cho khách thì chuyển thành phiếu; từ đó sổ không kết luận nữa.",
      the: ["voice", "tickets"],
    },
    {
      ten: "Xử lý phiếu",
      tone: "warning",
      ke: "Phiếu đi qua các bước của nó: giao làm lại ảnh, mở đơn chụp bù, hoặc gửi đề nghị hoàn tiền.",
      the: ["tickets"],
    },
    {
      ten: "Đóng phiếu",
      tone: "success",
      ke: "Việc bù cho khách đã xong, ghi kết luận để phiếu rời khỏi danh sách đang chạy.",
      the: ["tickets"],
    },
  ],
};

export const COMPLAINTS_GUIDE: MultiTabGuideConfig = {
  tabOrder: COMPLAINTS_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: COMPLAINTS_FLOW,
};
